/**
 * Advanced Guilt Calculator Algorithm
 * 
 * A sophisticated algorithm for calculating shopping guilt scores with the following features:
 * - Baseline score of 20%
 * - True essentials score near 0%
 * - Sophisticated categorization system
 * - Context-aware emotional analysis
 * - Smart handling of expendable income
 * - Pattern recognition for repeated purchases
 * - Fine-grained 1% increment scoring
 * - Robust handling with minimal user profile data
 */

/**
 * Helper function to shuffle an array
 * @param {Array} array - Array to shuffle
 * @returns {Array} Shuffled array
 */
function shuffleArray(array) {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
}

/**
 * Sophisticated item categorization with essentiality scoring and exact match fallback
 * @param {string} itemName - Name of the purchased item
 * @returns {Object} Category and essentiality information
 */
function categorizeItem(itemName) {
  if (!itemName || typeof itemName !== 'string') {
    return { category: "unknown", essentialScore: 10, isAbsoluteEssential: false };
  }
  
  const normalizedItemName = itemName.toLowerCase().trim();
  
  // Define categories with essentiality scores (0-100)
  const categories = [
    // ABSOLUTE ESSENTIALS (0-5% guilt)
    {
      category: "housing",
      keywords: ['rent', 'mortgage', 'lease', 'housing', 'property tax'],
      essentialScore: 98,
      isAbsoluteEssential: true
    },
    {
      category: "utilities",
      keywords: ['electric', 'electricity', 'water', 'gas', 'utility', 'utilities', 'internet', 
                 'phone bill', 'heating', 'cooling', 'sewage', 'garbage'],
      essentialScore: 95,
      isAbsoluteEssential: true
    },
    {
      category: "groceries",
      keywords: ['grocery', 'groceries', 'food', 'meal', 'bread', 'milk', 'egg', 'fruit', 'vegetable',
                 'meat', 'produce', 'protein', 'supermarket', 'farmer\'s market'],
      essentialScore: 90,
      isAbsoluteEssential: true
    },
    {
      category: "healthcare",
      keywords: ['medicine', 'prescription', 'healthcare', 'doctor', 'medical', 'health insurance', 
                 'hospital', 'clinic', 'therapy', 'medication', 'dentist', 'vision', 'emergency'],
      essentialScore: 95,
      isAbsoluteEssential: true
    },
    {
      category: "hygiene",
      keywords: ['soap', 'toothpaste', 'toilet paper', 'shampoo', 'deodorant', 'sanitary',
                 'hygiene', 'menstrual', 'diaper', 'dental', 'cleaning supplies'],
      essentialScore: 85,
      isAbsoluteEssential: true
    },
    // NECESSITIES (5-15% guilt)
    {
      category: "transportation",
      keywords: ['bus fare', 'train fare', 'subway', 'metro', 'commute', 'gas', 'petrol', 'fuel',
                 'car repair', 'tire', 'oil change', 'car service', 'car maintenance', 'brake', 'battery'],
      essentialScore: 80,
      isAbsoluteEssential: false
    },
    {
      category: "childcare",
      keywords: ['childcare', 'daycare', 'babysitting', 'child care', 'school supplies', 'tuition'],
      essentialScore: 85,
      isAbsoluteEssential: false
    },
    {
      category: "work_essentials",
      keywords: ['work clothes', 'uniform', 'work shoes', 'work equipment', 'professional', 'tools'],
      essentialScore: 75,
      isAbsoluteEssential: false
    },
    {
      category: "education",
      keywords: ['textbook', 'book', 'course', 'class', 'tuition', 'school', 'university', 'college',
                 'education', 'learning', 'training', 'seminar', 'study materials'],
      essentialScore: 70,
      isAbsoluteEssential: false
    },
    {
      category: "basic_clothing",
      keywords: ['underwear', 'socks', 'basic', 't-shirt', 'shirt', 'pants', 'jeans', 'shoes',
                 'winter coat', 'jacket', 'boots', 'rain gear'],
      essentialScore: 65,
      isAbsoluteEssential: false
    },
    // SEMI-NECESSITIES (15-35% guilt)
    {
      category: "home_basics",
      keywords: ['furniture', 'bed', 'mattress', 'chair', 'table', 'desk', 'appliance', 'refrigerator',
                 'stove', 'oven', 'washer', 'dryer', 'dishwasher', 'microwave', 'repair', 'maintenance'],
      essentialScore: 60,
      isAbsoluteEssential: false
    },
    {
      category: "gifts_necessary",
      keywords: ['gift', 'present', 'birthday', 'christmas', 'wedding', 'graduation', 'baby shower',
                 'anniversary', 'holiday gift', 'child birthday'],
      essentialScore: 50,
      isAbsoluteEssential: false
    },
    {
      category: "fitness_basic",
      keywords: ['exercise', 'fitness', 'gym', 'workout', 'health', 'running shoes', 'physical activity'],
      essentialScore: 45,
      isAbsoluteEssential: false
    },
    // NON-ESSENTIALS (35-65% guilt)
    {
      category: "entertainment",
      keywords: ['movie', 'ticket', 'concert', 'show', 'theater', 'theatre', 'streaming', 'subscription',
                 'netflix', 'spotify', 'disney', 'hulu', 'game', 'video game', 'hobby'],
      essentialScore: 35,
      isAbsoluteEssential: false
    },
    {
      category: "dining_out",
      keywords: ['restaurant', 'cafe', 'coffee shop', 'takeout', 'delivery', 'dining', 'lunch out',
                 'dinner out', 'fast food', 'coffee', 'brunch'],
      essentialScore: 30,
      isAbsoluteEssential: false
    },
    {
      category: "clothing_fashion",
      keywords: ['designer', 'fashion', 'trendy', 'style', 'branded', 'accessory', 'handbag',
                 'purse', 'sunglasses', 'sneakers', 'dress', 'suit', 'jewelry', 'watch'],
      essentialScore: 25,
      isAbsoluteEssential: false
    },
    {
      category: "electronics",
      keywords: ['phone', 'smartphone', 'laptop', 'computer', 'tablet', 'tv', 'television', 'headphones',
                 'earbuds', 'speaker', 'gadget', 'camera', 'gaming', 'console', 'smart home'],
      essentialScore: 20,
      isAbsoluteEssential: false
    },
    {
      category: "cosmetics",
      keywords: ['makeup', 'skincare', 'beauty', 'cosmetic', 'perfume', 'cologne', 'fragrance',
                 'facial', 'cream', 'lotion', 'exfoliant', 'mask'],
      essentialScore: 25,
      isAbsoluteEssential: false
    },
    // LUXURY/SPLURGES (65-90% guilt)
    {
      category: "luxury",
      keywords: ['spa', 'massage', 'vacation', 'trip', 'resort', 'hotel', 'travel', 'cruise',
                 'luxury', 'premium', 'gourmet', 'expensive', 'designer', 'high-end'],
      essentialScore: 10,
      isAbsoluteEssential: false
    },
    {
      category: "collectibles",
      keywords: ['collectible', 'collection', 'rare', 'limited edition', 'memorabilia', 'antique',
                 'vintage', 'art', 'statue', 'figure', 'poster', 'display'],
      essentialScore: 5,
      isAbsoluteEssential: false
    },
    {
      category: "impulse_items",
      keywords: ['snack', 'candy', 'chocolate', 'ice cream', 'dessert', 'treat', 'toy', 'trinket',
                 'souvenir', 'sale', 'clearance', 'discount', 'deal', 'bargain', 'impulse buy'],
      essentialScore: 10,
      isAbsoluteEssential: false
    }
  ];
  
  // First, check for exact match with category names
  for (const category of categories) {
    if (normalizedItemName === category.category) {
      return {
        category: category.category,
        essentialScore: category.essentialScore,
        isAbsoluteEssential: category.isAbsoluteEssential
      };
    }
  }
  
  // If no exact match, proceed with keyword matching
  let bestMatch = {
    category: "other",
    essentialScore: 20,
    isAbsoluteEssential: false
  };
  let highestMatchCount = 0;
  
  for (const category of categories) {
    const matchCount = category.keywords.filter(keyword => normalizedItemName.includes(keyword)).length;
    if (matchCount > highestMatchCount) {
      highestMatchCount = matchCount;
      bestMatch = {
        category: category.category,
        essentialScore: category.essentialScore,
        isAbsoluteEssential: category.isAbsoluteEssential
      };
    }
  }
  
  return bestMatch;
}

/**
 * Analyze emotional state during purchase
 * @param {string} mood - Reported mood during purchase
 * @returns {Object} Emotional factor analysis
 */
function analyzeEmotionalState(mood) {
  const emotionalFactors = {
    // Negative emotions (increase guilt)
    "bored": { score: 18, type: "negative", description: "Boredom-driven purchase" },
    "sad": { score: 20, type: "negative", description: "Emotionally consoling purchase" },
    "drunk": { score: 25, type: "negative", description: "Purchase with impaired judgment" },
    "fomo": { score: 22, type: "negative", description: "Fear-of-missing-out purchase" },
    
    // Positive emotions (decrease guilt)
    "happy": { score: -5, type: "positive", description: "Positive emotional purchase" },
    "celebrating": { score: -8, type: "positive", description: "Celebration purchase" },
    
    // Neutral/other emotions
    "cute": { score: 8, type: "neutral", description: "Aesthetically-driven purchase" }
  };
  
  return emotionalFactors[mood] || { score: 0, type: "unknown", description: "Unknown emotional state" };
}

/**
 * Calculate user's expendable income
 * @param {Object} userProfile - User profile data
 * @returns {number} Monthly expendable income
 */
function calculateExpendableIncome(userProfile) {
  if (!userProfile || !userProfile.incomeBracket) {
    return 1000; // Default assumption
  }
  
  // Base expendable income estimates by bracket
  const incomeEstimates = {
    'under-25k': 200,
    '25k-50k': 500,
    '50k-75k': 1000,
    '75k-100k': 2000,
    'over-100k': 4000
  };
  
  let expendableIncome = incomeEstimates[userProfile.incomeBracket] || 1000;
  
  // Adjust for family size if provided
  if (userProfile.familySize) {
    const dependents = Math.max(0, parseInt(userProfile.familySize) - 1);
    // Reduce by 15% per dependent
    expendableIncome *= Math.pow(0.85, dependents);
  }
  
  // Adjust for high cost of living areas if provided
  if (userProfile.locationCostFactor) {
    expendableIncome /= parseFloat(userProfile.locationCostFactor);
  }
  
  // Ensure minimum expendable income
  return Math.max(100, expendableIncome);
}

/**
 * Calculate price impact on guilt score
 * @param {number} amount - Purchase amount
 * @param {number} expendableIncome - Monthly expendable income
 * @param {Object} categoryInfo - Item category information
 * @returns {Object} Price impact analysis
 */
function calculatePriceImpact(amount, expendableIncome, categoryInfo) {
  // For absolute essentials, price doesn't impact guilt
  if (categoryInfo.isAbsoluteEssential) {
    return { score: 0, level: "none", description: "Essential purchase - price doesn't affect guilt" };
  }
  
  // Calculate as percentage of expendable income
  const incomePercentage = (amount / expendableIncome) * 100;
  
  // Essentiality factor (lower essentiality = higher guilt impact)
  const essentialityFactor = (100 - categoryInfo.essentialScore) / 100;
  
  let baseScore = 0;
  let level = "low";
  let description = "Minor price impact";
  
  // Determine base impact by percentage of expendable income
  if (incomePercentage > 50) {
    baseScore = 25;
    level = "extreme";
    description = "Extreme price impact - over 50% of expendable income";
  } else if (incomePercentage > 25) {
    baseScore = 20;
    level = "very high";
    description = "Very high price impact - over 25% of expendable income";
  } else if (incomePercentage > 15) {
    baseScore = 15;
    level = "high";
    description = "High price impact - over 15% of expendable income";
  } else if (incomePercentage > 10) {
    baseScore = 10;
    level = "moderate";
    description = "Moderate price impact - over 10% of expendable income";
  } else if (incomePercentage > 5) {
    baseScore = 5;
    level = "low";
    description = "Low price impact - over 5% of expendable income";
  } else {
    baseScore = 0;
    level = "minimal";
    description = "Minimal price impact - under 5% of expendable income";
  }
  
  // Scale by essentiality factor
  const finalScore = baseScore * essentialityFactor;
  
  return {
    score: finalScore,
    level,
    description,
    percentOfIncome: incomePercentage,
    essentialityFactor
  };
}

/**
 * Analyze usage frequency impact
 * @param {string} usageFrequency - How often the item will be used
 * @returns {Object} Usage factor analysis
 */
function analyzeUsageFrequency(usageFrequency) {
  const usageFactors = {
    "daily": { score: -10, level: "high", description: "Daily use significantly reduces guilt" },
    "weekly": { score: -5, level: "moderate", description: "Regular weekly use reduces guilt" },
    "occasionally": { score: 8, level: "low", description: "Occasional use adds some guilt" },
    "rarely": { score: 20, level: "very low", description: "Rare use adds significant guilt" },
    "never": { score: 30, level: "none", description: "Never using the item adds substantial guilt" }
  };
  
  return usageFactors[usageFrequency] || 
         { score: 0, level: "unknown", description: "Unknown usage frequency" };
}

/**
 * Analyze post-purchase satisfaction
 * @param {string} stillHappy - Whether still happy with purchase
 * @param {string} buyAgain - Whether would buy again
 * @returns {Object} Satisfaction factor analysis
 */
function analyzeSatisfaction(stillHappy, buyAgain) {
  let score = 0;
  let factors = [];
  
  // Still happy with purchase
  if (stillHappy === "yes") {
    score -= 8;
    factors.push("Still happy with purchase");
  } else if (stillHappy === "no") {
    score += 15;
    factors.push("Unhappy with purchase");
  }
  
  // Would buy again
  if (buyAgain === "hellyes") {
    score -= 5;
    factors.push("Would definitely buy again");
  } else if (buyAgain === "nope") {
    score += 12;
    factors.push("Would never buy again");
  }
  
  return {
    score,
    factors,
    description: factors.join(", ") || "Neutral satisfaction"
  };
}

/**
 * Analyze purchase patterns
 * @param {Object} currentPurchase - Current purchase data
 * @param {Array} purchaseHistory - Previous purchases
 * @returns {Object} Pattern factor analysis
 */
function analyzePatterns(currentPurchase, purchaseHistory) {
  if (!purchaseHistory || purchaseHistory.length === 0) {
    return { score: 0, factors: [], description: "No purchase history available" };
  }
  
  const currentCategory = categorizeItem(currentPurchase.item).category;
  const amount = parseFloat(currentPurchase.amount) || 0;
  
  let score = 0;
  let factors = [];
  
  // Find similar purchases in last month
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
  
  const recentSimilarPurchases = purchaseHistory.filter(purchase => {
    const purchaseDate = new Date(purchase.date);
    return purchaseDate >= oneMonthAgo && 
           categorizeItem(purchase.item).category === currentCategory;
  });
  
  // Add guilt for pattern of similar purchases (if not essential)
  if (!categorizeItem(currentPurchase.item).isAbsoluteEssential) {
    if (recentSimilarPurchases.length >= 5) {
      score += 15;
      factors.push(`${recentSimilarPurchases.length} similar purchases in the last month`);
    } else if (recentSimilarPurchases.length >= 3) {
      score += 10;
      factors.push(`${recentSimilarPurchases.length} similar purchases in the last month`);
    } else if (recentSimilarPurchases.length >= 1) {
      score += 5;
      factors.push(`${recentSimilarPurchases.length} similar purchases in the last month`);
    }
  }
  
  // Check for spending acceleration
  const twoWeeksAgo = new Date();
  twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
  
  const recentPurchases = purchaseHistory.filter(p => new Date(p.date) >= twoWeeksAgo);
  const recentTotal = recentPurchases.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
  
  // Compare to previous spending patterns
  const olderPurchases = purchaseHistory.filter(p => new Date(p.date) < twoWeeksAgo);
  
  if (olderPurchases.length > 0) {
    const oldestDate = new Date(Math.min(...olderPurchases.map(p => new Date(p.date).getTime())));
    const daysDiff = (twoWeeksAgo - oldestDate) / (1000 * 60 * 60 * 24);
    const twoWeekPeriods = Math.max(daysDiff / 14, 1);
    const averageTwoWeekSpending = olderPurchases.reduce((sum, p) => 
      sum + (parseFloat(p.amount) || 0), 0) / twoWeekPeriods;
    
    // Add guilt if recent spending is significantly higher than average
    if (recentTotal > averageTwoWeekSpending * 2) {
      score += 15;
      factors.push("Spending much more than usual (2x increase)");
    } else if (recentTotal > averageTwoWeekSpending * 1.5) {
      score += 10;
      factors.push("Spending notably more than usual (1.5x increase)");
    } else if (recentTotal > averageTwoWeekSpending * 1.2) {
      score += 5;
      factors.push("Spending slightly more than usual (1.2x increase)");
    }
  }
  
  return {
    score,
    factors,
    description: factors.join("; ") || "No concerning patterns detected"
  };
}

/**
 * Check if purchase was impulsive based on mood
 * @param {string} mood - Mood during purchase
 * @returns {boolean} Whether purchase was likely impulsive
 */
function isImpulsivePurchase(mood) {
  const impulsiveMoods = ["bored", "sad", "drunk", "fomo"];
  return impulsiveMoods.includes(mood);
}

/**
 * Comprehensive purchase analysis
 * @param {Object} data - Purchase data
 * @param {Object} userProfile - User profile data
 * @param {Array} purchaseHistory - Previous purchases
 * @returns {Object} Complete purchase analysis
 */
function analyzePurchase(data, userProfile = {}, purchaseHistory = []) {
  if (!data || !data.item) {
    throw new Error("Invalid purchase data: item name is required");
  }
  
  const analysis = {
    itemCategory: categorizeItem(data.item),
    emotionalFactor: analyzeEmotionalState(data.mood || "unknown"),
    priceImpact: calculatePriceImpact(
      parseFloat(data.amount) || 0,
      calculateExpendableIncome(userProfile),
      categorizeItem(data.item)
    ),
    usageFactor: analyzeUsageFrequency(data.usageFrequency || "unknown"),
    satisfactionFactor: analyzeSatisfaction(data.stillHappy || "unknown", data.buyAgain || "unknown"),
    patternFactor: analyzePatterns(data, purchaseHistory || []),
    isImpulsePurchase: isImpulsivePurchase(data.mood || "unknown")
  };
  
  return analysis;
}

/**
 * Master function to calculate guilt score
 * @param {Object} data - Purchase data
 * @param {Object} userProfile - User profile data (optional)
 * @param {Array} purchaseHistory - Previous purchases (optional)
 * @returns {Number} Guilt score between 0-100
 */
export const calculateGuiltScore = (data, userProfile = {}, purchaseHistory = []) => {
  let score = 20; // Baseline
  
  const analysis = analyzePurchase(data, userProfile, purchaseHistory);
  
  if (analysis.itemCategory.isAbsoluteEssential) {
    return Math.round(Math.min(3, score * 0.25));
  }
  
  score += (100 - analysis.itemCategory.essentialScore) * 0.15;
  score += analysis.emotionalFactor.score;
  score += analysis.priceImpact.score;
  score += analysis.usageFactor.score;
  score += analysis.satisfactionFactor.score;
  score += analysis.patternFactor.score;
  
  if (analysis.isImpulsePurchase && !analysis.itemCategory.isAbsoluteEssential) {
    score = Math.max(score, 20);
  }
  
  score = Math.max(0, Math.min(100, score));
  return Math.round(score);
};

/**
 * Correctly calculate guilt score based on purchase data
 * @param {Object} data - Purchase data with item, amount, mood, etc.
 * @returns {number} - Guilt score between 0-100
 */
export const calculateCorrectGuiltScore = (data) => {
  // Start with baseline score
  let score = 12.5;
  
  // Analyze item category
  const item = data.item.toLowerCase();
  
  // Check if item is an essential
  const isEssential = isEssentialItem(item);
  
  // If it's an essential item, cap the guilt score
  if (isEssential) {
    // Cap at 3% for essential items
    return Math.min(3, score * 0.25);
  }
  
  // Add emotional factor
  score += getEmotionalFactor(data.mood);
  
  // Add price impact
  score += getPriceImpact(parseFloat(data.amount) || 0);
  
  // Add usage factor
  score += getUsageFactor(data.usageFrequency);
  
  // Add satisfaction adjustment
  score += getSatisfactionFactor(data.stillHappy, data.buyAgain);
  
  // Ensure the score is between 0-100
  return Math.max(0, Math.min(100, Math.round(score)));
};

// Helper function to determine if item is essential
function isEssentialItem(item) {
  // List of essential categories and keywords
  const essentialKeywords = [
    // Food/Groceries
    'grocery', 'groceries', 'food', 'meal', 'bread', 'milk', 'egg', 'fruit', 'vegetable',
    'meat', 'produce', 'protein', 'supermarket',
    
    // Housing
    'rent', 'mortgage', 'lease', 'housing', 'property tax',
    
    // Utilities
    'electric', 'electricity', 'water', 'gas', 'utility', 'utilities', 'internet', 
    'phone bill', 'heating', 'cooling', 'sewage', 'garbage',
    
    // Healthcare
    'medicine', 'prescription', 'healthcare', 'doctor', 'medical', 'health insurance', 
    'hospital', 'clinic', 'therapy', 'medication'
  ];
  
  // Check if item contains any essential keywords
  return essentialKeywords.some(keyword => item.includes(keyword));
}

// Helper function for emotional factor
function getEmotionalFactor(mood) {
  const emotionalFactors = {
    // Negative emotions (increase guilt)
    "bored": 18,
    "sad": 20,
    "drunk": 25,
    "fomo": 22,
    
    // Positive emotions (decrease guilt)
    "happy": -5,
    "celebrating": -8,
    
    // Neutral/other emotions
    "cute": 8
  };
  
  return emotionalFactors[mood] || 0;
}

// Helper function for price impact
function getPriceImpact(amount) {
  if (amount > 500) return 25;
  if (amount > 200) return 15;
  if (amount > 100) return 10;
  if (amount > 50) return 5;
  if (amount < 20) return -10;
  return 0;
}

// Helper function for usage factor
function getUsageFactor(usageFrequency) {
  const usageFactors = {
    "daily": -10,
    "weekly": -5,
    "occasionally": 8,
    "rarely": 20,
    "never": 30
  };
  
  return usageFactors[usageFrequency] || 0;
}

// Helper function for satisfaction factor
function getSatisfactionFactor(stillHappy, buyAgain) {
  let score = 0;
  
  // Still happy with purchase
  if (stillHappy === "yes") {
    score -= 8;
  } else if (stillHappy === "no") {
    score += 15;
  }
  
  // Would buy again
  if (buyAgain === "hellyes") {
    score -= 5;
  } else if (buyAgain === "nope") {
    score += 12;
  }
  
  return score;
}

/**
 * Provide default challenges for new users
 * @returns {Array} Default challenges
 */
function getDefaultChallenges() {
  return [
    {
      id: "24-hour-rule",
      title: "24-Hour Rule Challenge",
      description: "Wait 24 hours before completing any non-essential purchase over $50",
      difficulty: "easy",
      reward: "Reduced impulse buys and more thoughtful spending"
    },
    {
      id: "track-week",
      title: "Full Tracking Week",
      description: "Log every single purchase you make for one week, no matter how small",
      difficulty: "easy",
      reward: "Complete spending awareness and baseline data"
    },
    {
      id: "shopping-list",
      title: "List Loyalty Challenge",
      description: "Only buy items that were on your shopping list before you entered the store or opened a shopping website",
      difficulty: "medium",
      reward: "Focused shopping habits and reduced impulse buys"
    },
    {
      id: "gratitude-practice",
      title: "Shopping Gratitude Practice",
      description: "List 3 things you're grateful for that you already own before making any new purchase",
      difficulty: "easy",
      reward: "Increased appreciation for what you have"
    },
    {
      id: "needs-vs-wants",
      title: "Needs vs. Wants Categorization",
      description: "Before each purchase, explicitly categorize it as a need or a want and justify your decision",
      difficulty: "medium", 
      reward: "Clearer priorities and more intentional spending"
    }
  ];
}

/**
 * Generate shopping challenges based on purchase history
 * @param {Array} purchaseHistory - Previous purchases
 * @param {Object} userProfile - User profile for personalization
 * @returns {Array} Personalized challenges
 */
export const generateChallenges = (purchaseHistory, userProfile = {}) => {
  // If not enough purchase history, provide default challenges
  if (!purchaseHistory || purchaseHistory.length < 3) {
    return getDefaultChallenges();
  }
  
  const challenges = [];
  
  // Analyze spending patterns
  const totalSpent = purchaseHistory.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
  
  // Find emotional purchases
  const emotionalPurchases = purchaseHistory.filter(p => 
    ["bored", "sad", "drunk", "fomo"].includes(p.mood)
  );
  const emotionalPercentage = (emotionalPurchases.length / purchaseHistory.length) * 100;
  
  // Find highest guilt category
  const categoryGuilt = {};
  
  purchaseHistory.forEach(purchase => {
    const category = categorizeItem(purchase.item).category;
    if (!categoryGuilt[category]) {
      categoryGuilt[category] = { total: 0, count: 0, amount: 0 };
    }
    
    categoryGuilt[category].total += purchase.guiltScore;
    categoryGuilt[category].count += 1;
    categoryGuilt[category].amount += parseFloat(purchase.amount || 0);
  });
  
  // Calculate average guilt by category
  Object.keys(categoryGuilt).forEach(category => {
    categoryGuilt[category].average = categoryGuilt[category].total / categoryGuilt[category].count;
  });
  
  // Sort categories by average guilt
  const sortedCategories = Object.entries(categoryGuilt)
    .filter(([, data]) => data.count >= 2) // At least 2 purchases
    .sort((a, b) => b[1].average - a[1].average);
  
  // Get most common category
  const categoryCounts = {};
  purchaseHistory.forEach(purchase => {
    const category = categorizeItem(purchase.item).category;
    categoryCounts[category] = (categoryCounts[category] || 0) + 1;
  });
  
  const mostCommonCategory = Object.entries(categoryCounts)
    .sort((a, b) => b[1] - a[1])[0];
  
  // Add emotional spending challenges
  if (emotionalPercentage > 30) {
    challenges.push({
      id: "emotion-tracking",
      title: "Emotion Tracking Challenge",
      description: "Log your feelings before and after every purchase for a week",
      difficulty: "easy",
      reward: "Better understanding of your emotional triggers"
    });
    
    challenges.push({
      id: "mood-alternatives",
      title: "Find Mood-Boosting Alternatives",
      description: "Discover three free activities that boost your mood when you're feeling low",
      difficulty: "medium",
      reward: "Healthier coping strategies for emotional times"
    });
  }
  
  // Add high-guilt category challenge
  if (sortedCategories.length > 0 && sortedCategories[0][1].average > 50) {
    const highestGuiltCategory = sortedCategories[0][0];
    const readableCategory = highestGuiltCategory
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
    
    challenges.push({
      id: `${highestGuiltCategory}-pause`,
      title: `${readableCategory} Pause`,
      description: `Take a two-week break from buying anything in the ${readableCategory.toLowerCase()} category`,
      difficulty: "hard",
      reward: "Breaking the cycle of guilt-inducing purchases"
    });
  }
  
  // Add most common category challenge
  if (mostCommonCategory && mostCommonCategory[1] > 3 && 
      categorizeItem(mostCommonCategory[0]).essentialScore < 70) {
    const readableCategory = mostCommonCategory[0]
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
    
    challenges.push({
      id: "category-inventory",
      title: "Category Inventory",
      description: `Make a complete inventory of all your ${readableCategory.toLowerCase()} items`,
      difficulty: "medium",
      reward: "Better awareness of what you already own"
    });
  }
  
  // Add general challenges based on spending patterns
  challenges.push({
    id: "24-hour-rule",
    title: "24-Hour Rule Challenge",
    description: "Wait 24 hours before completing any non-essential purchase over $50",
    difficulty: "easy",
    reward: "Reduced impulse buys and more thoughtful spending"
  });
  
  challenges.push({
    id: "needs-vs-wants",
    title: "Needs vs. Wants Categorization",
    description: "Before each purchase, explicitly categorize it as a need or a want and justify your decision",
    difficulty: "medium",
    reward: "Clearer priorities and more intentional spending"
  });
  
  challenges.push({
    id: "cash-only",
    title: "Cash-Only Week",
    description: "Use only cash for all discretionary spending for one week",
    difficulty: "hard",
    reward: "Stronger connection to your spending"
  });
  
  challenges.push({
    id: "gratitude-practice",
    title: "Shopping Gratitude Practice",
    description: "List 3 things you're grateful for that you already own before making any new purchase",
    difficulty: "easy",
    reward: "Increased appreciation for what you have"
  });
  
  challenges.push({
    id: "no-spend-weekend",
    title: "No-Spend Weekend",
    description: "Go an entire weekend without spending any money on non-essentials",
    difficulty: "medium",
    reward: "Financial reset and mindfulness"
  });

  challenges.push({
    id: "shop-mindfully",
    title: "Mindful Shopping Challenge",
    description: "Check out our recommended mindful shopping products and choose one alternative to a recent impulse buy.",
    difficulty: "easy",
    reward: "Discover alternatives to impulsive spending"
  });
  
  // Shuffle and return up to 5 challenges
  return shuffleArray(challenges).slice(0, 5);
};

/**
 * Generate personalized advice based on purchase and analysis
 * @param {Object} purchase - Purchase data
 * @param {Object} analysis - Purchase analysis
 * @returns {Object} Personalized advice
 */
export const generateAdvice = (purchase, analysis) => {
  const score = purchase.guiltScore;
  
  // No or very low guilt (0-10%)
  if (score <= 10) {
    return {
      emoji: "✅",
      title: "Guilt-Free Purchase",
      message: `Your ${purchase.item} purchase was essential or very reasonable.`,
      tips: [
        "Continue prioritizing necessities in your budget",
        "Consider tracking how this essential purchase contributes to your well-being",
        "Setting up automatic payments for recurring essentials can help budget management"
      ]
    };
  }
  // Low guilt (11-25%)
  else if (score <= 25) {
    return {
      emoji: "👍",
      title: "Low Guilt - Good Decision",
      message: `Your ${purchase.item} purchase appears to be a thoughtful decision.`,
      tips: [
        "Keep balancing needs versus wants in your spending decisions",
        "Consider creating a specific budget category for these types of purchases",
        "Look for opportunities to optimize similar purchases in the future"
      ]
    };
  }
  // Moderate guilt (26-50%)
  else if (score <= 50) {
    return {
      emoji: "🤔",
      title: "Moderate Guilt - Mixed Value",
      message: `Your ${purchase.item} purchase has some questionable aspects.`,
      tips: [
        "Next time, consider waiting 24 hours before similar purchases",
        "Track your usage to confirm whether this purchase was worthwhile",
        "Look for ways to maximize the value you get from this item"
      ]
    };
  }
  // High guilt (51-75%)
  else if (score <= 75) {
    return {
      emoji: "😬",
      title: "High Guilt - Questionable Purchase",
      message: `Your ${purchase.item} purchase may not have been the best decision.`,
      tips: [
        "Try implementing a waiting period for future purchases in this category",
        "Consider setting a monthly budget limit for non-essential items",
        "Before similar purchases, ask yourself if it aligns with your priorities"
      ]
    };
  }
  // Very high guilt (76-100%)
  else {
    return {
      emoji: "😱",
      title: "Very High Guilt - Regrettable Purchase",
      message: `Your ${purchase.item} purchase shows multiple concerning factors.`,
      tips: [
        "Consider if this item can be returned or resold",
        "For future purchases, create a pros/cons list before buying",
        "Try a 30-day no-spend challenge for this category",
        "Identify emotional triggers that led to this purchase and develop alternative coping strategies"
      ]
    };
  }
};

/**
 * Comprehensive analysis of spending patterns
 * @param {Array} purchases - Purchase history
 * @returns {Object} Detailed spending analysis
 */
export const analyzeSpendingPatterns = (purchases) => {
  if (!purchases || purchases.length < 3) return null;
  
  // Calculate total spent
  const totalSpent = purchases.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
  
  // Calculate average guilt
  const avgGuilt = purchases.reduce((sum, p) => sum + p.guiltScore, 0) / purchases.length;
  
  // Identify most common emotional state when shopping
  const moodCounts = purchases.reduce((acc, p) => {
    acc[p.mood] = (acc[p.mood] || 0) + 1;
    return acc;
  }, {});
  
  const topMood = Object.entries(moodCounts)
    .sort((a, b) => b[1] - a[1])[0][0];
  
  // Calculate essential vs. non-essential spending
  const essentialPurchases = purchases.filter(p => 
    categorizeItem(p.item).isAbsoluteEssential
  );
  
  const essentialTotal = essentialPurchases.reduce((sum, p) => 
    sum + parseFloat(p.amount || 0), 0);
  
  const essentialPercentage = (essentialTotal / totalSpent) * 100;
  
  // Calculate emotional spending
  const emotionalPurchases = purchases.filter(p => 
    ["bored", "sad", "drunk", "fomo"].includes(p.mood)
  );
  
  const emotionalTotal = emotionalPurchases.reduce((sum, p) => 
    sum + parseFloat(p.amount || 0), 0);
  
  const emotionalPercentage = (emotionalTotal / totalSpent) * 100;
  
  // Calculate frequently vs. rarely used
  const frequentlyUsed = purchases.filter(p => 
    p.usageFrequency === "daily" || p.usageFrequency === "weekly"
  );
  
  const rarelyUsed = purchases.filter(p => 
    p.usageFrequency === "rarely" || p.usageFrequency === "never"
  );
  
  const frequentlyUsedTotal = frequentlyUsed.reduce((sum, p) => 
    sum + parseFloat(p.amount || 0), 0);
  
  const rarelyUsedTotal = rarelyUsed.reduce((sum, p) => 
    sum + parseFloat(p.amount || 0), 0);
  
  const frequentlyUsedPercentage = (frequentlyUsedTotal / totalSpent) * 100;
  const rarelyUsedPercentage = (rarelyUsedTotal / totalSpent) * 100;
  
  // Calculate spending by category
  const categorySpending = {};
  
  purchases.forEach(purchase => {
    const category = categorizeItem(purchase.item).category;
    if (!categorySpending[category]) {
      categorySpending[category] = {
        amount: 0,
        count: 0,
        totalGuilt: 0
      };
    }
    
    categorySpending[category].amount += parseFloat(purchase.amount || 0);
    categorySpending[category].count += 1;
    categorySpending[category].totalGuilt += purchase.guiltScore;
  });
  
  // Calculate average guilt and percentage for each category
  Object.keys(categorySpending).forEach(category => {
    categorySpending[category].averageGuilt = 
      categorySpending[category].totalGuilt / categorySpending[category].count;
    
    categorySpending[category].percentage = 
      (categorySpending[category].amount / totalSpent) * 100;
  });
  
  // Sort categories by spending amount
  const categoryBreakdown = Object.entries(categorySpending)
    .map(([category, data]) => ({
      category,
      readableCategory: category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      amount: data.amount,
      count: data.count,
      percentage: data.percentage,
      averageGuilt: data.averageGuilt
    }))
    .sort((a, b) => b.amount - a.amount);
  
  // Calculate guilt trend (is it improving or worsening?)
  const orderedByDate = [...purchases].sort((a, b) => 
    new Date(a.date) - new Date(b.date)
  );
  
  // Split into halves to see if guilt is trending up or down
  const halfwayPoint = Math.floor(orderedByDate.length / 2);
  const olderPurchases = orderedByDate.slice(0, halfwayPoint);
  const newerPurchases = orderedByDate.slice(halfwayPoint);
  
  const olderAvgGuilt = olderPurchases.reduce((sum, p) => 
    sum + p.guiltScore, 0) / olderPurchases.length;
  
  const newerAvgGuilt = newerPurchases.reduce((sum, p) => 
    sum + p.guiltScore, 0) / newerPurchases.length;
  
  const guiltTrend = newerAvgGuilt < olderAvgGuilt ? "improving" : "worsening";
  const guiltTrendPercentage = Math.abs(((newerAvgGuilt - olderAvgGuilt) / olderAvgGuilt) * 100);
  
  // Identify strengths and areas for improvement
  const strengths = [];
  const improvements = [];
  
  if (essentialPercentage > 50) {
    strengths.push("Most spending is on essential items");
  } else {
    improvements.push("Increase the proportion of spending on essentials");
  }
  
  if (emotionalPercentage < 20) {
    strengths.push("Low emotional-triggered spending");
  } else {
    improvements.push("Reduce purchases made when feeling emotional");
  }
  
  if (frequentlyUsedPercentage > 70) {
    strengths.push("Most purchases are frequently used");
  }
  
  if (rarelyUsedPercentage > 15) {
    improvements.push("Reduce purchases of rarely used items");
  }
  
  if (guiltTrend === "improving" && guiltTrendPercentage > 10) {
    strengths.push("Shopping guilt is decreasing over time");
  } else if (guiltTrend === "worsening" && guiltTrendPercentage > 10) {
    improvements.push("Work on reducing shopping guilt over time");
  }
  
  // Return the complete analysis
  return {
    totalSpent,
    avgGuilt,
    topMood,
    essential: {
      total: essentialTotal,
      percentage: essentialPercentage,
      count: essentialPurchases.length
    },
    emotional: {
      total: emotionalTotal,
      percentage: emotionalPercentage,
      count: emotionalPurchases.length
    },
    usage: {
      frequentlyUsed: {
        total: frequentlyUsedTotal,
        percentage: frequentlyUsedPercentage,
        count: frequentlyUsed.length
      },
      rarelyUsed: {
        total: rarelyUsedTotal,
        percentage: rarelyUsedPercentage,
        count: rarelyUsed.length
      }
    },
    categories: categoryBreakdown,
    guiltTrend: {
      direction: guiltTrend,
      percentage: guiltTrendPercentage,
      older: olderAvgGuilt,
      newer: newerAvgGuilt
    },
    purchaseCount: purchases.length,
    strengths,
    improvements
  };
};

/**
 * Calculate potential savings based on purchase history
 * @param {Array} purchases - Purchase history
 * @returns {Object} Savings potential analysis
 */
export const calculateSavingsPotential = (purchases) => {
  if (!purchases || purchases.length === 0) return null;
  
  // Categorize purchases by guilt level
  const highGuiltPurchases = purchases.filter(p => p.guiltScore > 70);
  const mediumGuiltPurchases = purchases.filter(p => p.guiltScore >= 40 && p.guiltScore <= 70);
  const lowGuiltPurchases = purchases.filter(p => p.guiltScore < 40);
  
  // Identify emotional purchases
  const emotionalPurchases = purchases.filter(p => 
    (p.mood === "bored" || p.mood === "sad" || p.mood === "drunk" || p.mood === "fomo") &&
    p.guiltScore > 30 &&
    !categorizeItem(p.item).isAbsoluteEssential
  );
  
  // Calculate totals for each category
  const highGuiltTotal = highGuiltPurchases.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
  const mediumGuiltTotal = mediumGuiltPurchases.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
  const lowGuiltTotal = lowGuiltPurchases.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
  const emotionalTotal = emotionalPurchases.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
  
  // Calculate time span of purchases in months
  const dates = purchases.map(p => new Date(p.date));
  const oldestDate = new Date(Math.min(...dates.map(d => d.getTime())));
  const newestDate = new Date(Math.max(...dates.map(d => d.getTime())));
  const monthsDiff = (newestDate.getFullYear() - oldestDate.getFullYear()) * 12 +
                    newestDate.getMonth() - oldestDate.getMonth();
  const monthsSpan = Math.max(1, monthsDiff); // Minimum 1 month
  
  // Calculate monthly averages
  const monthlyHighGuilt = highGuiltTotal / monthsSpan;
  const monthlyMediumGuilt = mediumGuiltTotal / monthsSpan;
  const monthlyLowGuilt = lowGuiltTotal / monthsSpan;
  const monthlyEmotional = emotionalTotal / monthsSpan;
  
  // Calculate realistic savings potential (not all high-guilt purchases can be avoided)
  const monthlySavingsPotential = 
    (monthlyHighGuilt * 0.75) +    // Can avoid 75% of high guilt purchases
    (monthlyMediumGuilt * 0.4) +   // Can avoid 40% of medium guilt purchases
    (monthlyEmotional * 0.6);      // Can avoid 60% of emotional purchases (with overlap)
  
  const yearlySavingsPotential = monthlySavingsPotential * 12;
  const fiveYearSavingsPotential = yearlySavingsPotential * 5;
  
  // Calculate investment growth potential (7% annual return)
  const yearlyInvestmentGrowth = yearlySavingsPotential * 1.07;
  const fiveYearInvestmentGrowth = yearlySavingsPotential * Math.pow(1.07, 5);
  
  return {
    highGuilt: {
      total: highGuiltTotal,
      monthly: monthlyHighGuilt,
      count: highGuiltPurchases.length
    },
    mediumGuilt: {
      total: mediumGuiltTotal,
      monthly: monthlyMediumGuilt,
      count: mediumGuiltPurchases.length
    },
    lowGuilt: {
      total: lowGuiltTotal,
      monthly: monthlyLowGuilt,
      count: lowGuiltPurchases.length
    },
    emotional: {
      total: emotionalTotal,
      monthly: monthlyEmotional,
      count: emotionalPurchases.length
    },
    savingsPotential: {
      monthly: monthlySavingsPotential,
      yearly: yearlySavingsPotential,
      fiveYear: fiveYearSavingsPotential,
      yearlyInvestment: yearlyInvestmentGrowth,
      fiveYearInvestment: fiveYearInvestmentGrowth
    },
    monthsAnalyzed: monthsSpan
  };
};
